import React, { useMemo } from 'react'

import clsx from 'clsx'
import { parsePhoneNumberWithError } from 'libphonenumber-js'

import GoogleIcon from '$commonSrc/google-icon.svg'
import ActionButton, {
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import { enroll2faSmsPath } from '$extensionSrc/consts/urlConsts'

import UserSettingsRow from './UserSettingsRow'

import './UserSettingsCredentialRow.scss' // Also depends on styles inherited from UserSettingsPage

export interface UserSettingsCredentialSpec {
  credentialType: 'email-password' | 'oauth'
  email: string
  emailConfirmed: boolean
  handleRemove2fa: () => void
  twofaMethod: 'app' | 'sms' | null
  twofaPhoneNumber: string | null
  openIdIssuer: string | null
}

function formatPhoneNumber(phoneNumber: string): string {
  const formattedNumber =
    parsePhoneNumberWithError(phoneNumber).formatNational()
  const lastFourDigits = formattedNumber.slice(-4)
  const toBeMasked = formattedNumber.slice(0, -4)
  return toBeMasked.replace(/\d/g, '*') + lastFourDigits
}

function TwofaRow({ twofaMethod, twofaPhoneNumber, handleRemove2fa }) {
  const currentUserTwofaDescription = useMemo(() => {
    switch (twofaMethod) {
      case 'app':
        return 'Get code from my authenticator app'
      case 'sms':
        return `Send code to ${formatPhoneNumber(twofaPhoneNumber)}`
      default:
        return 'None'
    }
  }, [twofaMethod, twofaPhoneNumber])

  return (
    <UserSettingsRow>
      <div className="d-flex flex-column">
        <div className="body1 bold w-100">Two-factor authentication</div>
        <div className="body1">{currentUserTwofaDescription}</div>
      </div>
      {twofaMethod ? (
        <ActionButton
          className="edit-button"
          onClick={handleRemove2fa}
          type={ActionButtonType.SECONDARY}
        >
          Remove
        </ActionButton>
      ) : (
        <ActionButton
          className="edit-button"
          onClick={() => {
            window.location.assign(enroll2faSmsPath)
          }}
          type={ActionButtonType.SECONDARY}
        >
          Set up
        </ActionButton>
      )}
    </UserSettingsRow>
  )
}

function OpenIdIssuerInfo({ oauthIssuer }) {
  let details
  if (oauthIssuer === 'accounts.google.com') {
    details = (
      <>
        Logged in with Google
        <GoogleIcon aria-hidden className="google-icon" />
      </>
    )
  } else {
    details = <>Logged in with {oauthIssuer}</>
  }

  return <div className="issuer-details-container body3">{details}</div>
}

export default function UserSettingsCredentialRow({
  credentialType,
  email,
  emailConfirmed,
  handleRemove2fa,
  twofaMethod,
  twofaPhoneNumber,
  openIdIssuer,
}: UserSettingsCredentialSpec) {
  let unrecognizedCredentialType
  let openIdIssuerComponent
  let twofaComponent

  switch (credentialType) {
    case 'email-password':
      twofaComponent = (
        <TwofaRow
          twofaMethod={twofaMethod}
          twofaPhoneNumber={twofaPhoneNumber}
          handleRemove2fa={handleRemove2fa}
        />
      )
      break
    case 'oauth':
      openIdIssuerComponent = <OpenIdIssuerInfo oauthIssuer={openIdIssuer} />
      break
    default:
      unrecognizedCredentialType = true
  }

  if (unrecognizedCredentialType) {
    return null
  }

  return (
    <div className="UserSettingsCredentialRow">
      <UserSettingsRow className={clsx(twofaComponent && 'no-border')}>
        <div className="d-flex flex-column">
          <div className="body1 bold">
            {emailConfirmed ? 'Email address' : 'Email address (unconfirmed)'}
          </div>
          <div className="body1">{email}</div>
        </div>
        {openIdIssuerComponent}
      </UserSettingsRow>
      {twofaComponent && <div className="twofa-divider" />}
      {twofaComponent}
    </div>
  )
}
